(function (window, document) {
  if (window.EPPromoCode && typeof window.EPPromoCode.init === 'function') {
    window.EPPromoCode.init(document);
    return;
  }

  const instances = new WeakMap();
  const floatingDismissDuration = 7 * 24 * 60 * 60 * 1000;
  const floatingAnimationDuration = 250;

  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        // Use the selection fallback when clipboard permission is denied.
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }

    textarea.remove();
    return copied;
  };

  const formatCountdown = (remainingSeconds) => {
    const secondsPerDay = 24 * 60 * 60;
    const days = Math.floor(remainingSeconds / secondsPerDay);
    const hours = Math.floor((remainingSeconds % secondsPerDay) / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;
    const secondsLabel = `${String(seconds).padStart(2, '0')} s`;

    if (days > 0) {
      return `${days} j ${String(hours).padStart(2, '0')} h ${String(minutes).padStart(2, '0')} min ${secondsLabel}`;
    }

    if (hours > 0) {
      return `${String(hours).padStart(2, '0')} h ${String(minutes).padStart(2, '0')} min ${secondsLabel}`;
    }

    return `${minutes} min ${secondsLabel}`;
  };

  const clearCountdown = (instance) => {
    if (instance.countdownInterval) {
      window.clearInterval(instance.countdownInterval);
      instance.countdownInterval = null;
    }
  };

  const syncBannerBodyClass = () => {
    const visibleBanner = document.querySelector(
      '[data-ep-promo-code].ep-promo-code--compact:not([hidden]), [data-ep-static-promo-banner]:not([data-ep-static-promo-closed="true"])'
    );
    document.body.classList.toggle('has-promo-banner', Boolean(visibleBanner));
  };

  const finishHide = (instance) => {
    clearCountdown(instance);
    instance.hideTimer = null;
    instance.root.hidden = true;
    instance.root.setAttribute('aria-hidden', 'true');
    instance.root.classList.remove('is-visible', 'is-closing');
    syncBannerBodyClass();
  };

  const hideRoot = (instance, animate = false) => {
    if (!animate || !instance.floating || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishHide(instance);
      return;
    }

    clearCountdown(instance);
    window.clearTimeout(instance.hideTimer);
    instance.root.classList.remove('is-visible');
    instance.root.classList.add('is-closing');
    instance.hideTimer = window.setTimeout(() => finishHide(instance), floatingAnimationDuration);
  };

  const readFloatingSuppression = (key) => {
    if (!key) return false;

    try {
      const value = window.localStorage.getItem(key);
      if (!value) return false;

      const record = JSON.parse(value);
      if (record && Number(record.expiresAt) > Date.now()) return true;

      window.localStorage.removeItem(key);
    } catch (error) {
      return false;
    }

    return false;
  };

  const writeFloatingSuppression = (key) => {
    if (!key) return;

    try {
      window.localStorage.setItem(key, JSON.stringify({ expiresAt: Date.now() + floatingDismissDuration }));
    } catch (error) {
      // The offer remains suppressed for the current session when storage is unavailable.
    }
  };

  const readSessionShown = (key) => {
    if (!key) return false;

    try {
      return window.sessionStorage.getItem(key) === 'true';
    } catch (error) {
      return false;
    }
  };

  const writeSessionShown = (key) => {
    if (!key) return;

    try {
      window.sessionStorage.setItem(key, 'true');
    } catch (error) {
      // The in-memory flag still prevents a second reveal in this page.
    }
  };

  const clearFloatingTrigger = (instance) => {
    window.clearTimeout(instance.revealTimer);
    instance.revealTimer = null;
    if (instance.scrollHandler) {
      window.removeEventListener('scroll', instance.scrollHandler);
      instance.scrollHandler = null;
    }
  };

  const revealRoot = (instance) => {
    if (instance.suppressed || !instance.floating || !instance.root.hidden) return;

    instance.root.hidden = false;
    instance.root.removeAttribute('aria-hidden');
    instance.root.classList.remove('is-closing');
    syncBannerBodyClass();

    const reveal = () => {
      if (!instance.root.hidden) instance.root.classList.add('is-visible');
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      reveal();
    } else {
      window.requestAnimationFrame(reveal);
    }

    initCountdown(instance);
  };

  const initCopy = (instance) => {
    if (!instance.button || !instance.label || !instance.code) return;

    instance.button.addEventListener('click', async () => {
      const copied = await copyText(instance.code);
      const label = copied ? instance.button.dataset.labelSuccess : instance.button.dataset.labelError;

      instance.button.classList.toggle('is-copied', copied);
      instance.label.textContent = label;
      instance.button.setAttribute('aria-label', label);
      if (instance.status) {
        instance.status.textContent = copied
          ? `Code ${instance.code} copié dans le presse-papiers.`
          : `Copie impossible. Sélectionnez le code ${instance.code} manuellement.`;
      }

      if (copied && instance.floating) {
        writeFloatingSuppression(instance.dismissKey);
        if (!instance.copyTimer) {
          instance.copyTimer = window.setTimeout(() => hideRoot(instance, true), 900);
        }
      }
    });
  };

  const initDismiss = (instance) => {
    if (!instance.dismissButton || !instance.dismissKey) return;

    if (instance.floating) {
      if (readFloatingSuppression(instance.dismissKey)) {
        instance.suppressed = true;
        hideRoot(instance);
      }

      if (instance.suppressed) return;

      instance.dismissButton.addEventListener('click', () => {
        instance.suppressed = true;
        writeFloatingSuppression(instance.dismissKey);
        clearFloatingTrigger(instance);
        hideRoot(instance, true);
      });
      return;
    }

    let isDismissed = false;
    try {
      isDismissed = window.localStorage.getItem(instance.dismissKey) === 'true';
    } catch (error) {
      isDismissed = false;
    }

    if (isDismissed) {
      hideRoot(instance);
      return;
    }

    instance.dismissButton.addEventListener('click', () => {
      hideRoot(instance);
      try {
        window.localStorage.setItem(instance.dismissKey, 'true');
      } catch (error) {
        // The offer can still be dismissed when storage is unavailable.
      }
    });
  };

  const initCountdown = (instance) => {
    if (!instance.countdown || !instance.countdownValue) return;

    const endTimestamp = Number(instance.root.dataset.endTimestamp);
    if (!Number.isFinite(endTimestamp) || endTimestamp <= 0) return;

    const updateCountdown = () => {
      if (!document.body.contains(instance.root)) {
        clearCountdown(instance);
        return false;
      }

      const remainingMilliseconds = (endTimestamp * 1000) - Date.now();
      if (remainingMilliseconds <= 0) {
        hideRoot(instance);
        return false;
      }

      const remainingSeconds = Math.ceil(remainingMilliseconds / 1000);
      const duration = formatCountdown(remainingSeconds);

      if (instance.countdownValue.textContent !== duration) {
        instance.countdownValue.textContent = duration;
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          instance.countdownValue.classList.remove('is-ticking');
          window.requestAnimationFrame(() => instance.countdownValue.classList.add('is-ticking'));
        }
      }

      instance.countdown.setAttribute('aria-label', `Se termine dans ${duration}`);
      instance.countdown.hidden = false;
      return true;
    };

    if (updateCountdown()) {
      instance.countdownInterval = window.setInterval(() => {
        if (!updateCountdown()) clearCountdown(instance);
      }, 1000);
    }
  };

  const initRoot = (root) => {
    if (!root || instances.has(root)) return;

    const instance = {
      root,
      button: root.querySelector('[data-ep-promo-copy]'),
      label: root.querySelector('[data-ep-promo-copy-label]'),
      status: root.querySelector('[data-ep-promo-status]'),
      code: (root.dataset.code || '').trim(),
      countdown: root.querySelector('[data-ep-promo-countdown]'),
      countdownValue: root.querySelector('[data-ep-promo-countdown-value]'),
      dismissButton: root.querySelector('[data-ep-promo-dismiss]'),
      dismissKey: root.dataset.epPromoDismissKey,
      sessionKey: root.dataset.epPromoSessionKey,
      floating: root.hasAttribute('data-ep-promo-floating'),
      suppressed: false,
      triggered: false,
      revealTimer: null,
      scrollHandler: null,
      hideTimer: null,
      copyTimer: null,
      countdownInterval: null,
    };

    instances.set(root, instance);
    initCopy(instance);
    initDismiss(instance);
    if (instance.floating) {
      if (!instance.suppressed && readSessionShown(instance.sessionKey || instance.dismissKey)) {
        instance.suppressed = true;
        hideRoot(instance);
      } else if (!instance.suppressed) {
        const triggerReveal = () => {
          if (instance.suppressed || instance.triggered) return;
          instance.triggered = true;
          clearFloatingTrigger(instance);
          writeSessionShown(instance.sessionKey || instance.dismissKey);
          revealRoot(instance);
        };

        instance.revealTimer = window.setTimeout(triggerReveal, 6000);
        instance.scrollHandler = () => {
          const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollableHeight > 0 && window.scrollY / scrollableHeight >= 0.3) triggerReveal();
        };
        window.addEventListener('scroll', instance.scrollHandler, { passive: true });
      }
    } else if (!root.hidden) {
      initCountdown(instance);
    }
    syncBannerBodyClass();
  };

  const init = (container) => {
    const scope = container || document;
    if (scope.matches && scope.matches('[data-ep-promo-code]')) initRoot(scope);
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll('[data-ep-promo-code]').forEach(initRoot);
  };

  window.EPPromoCode = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})(window, document);
